import { Snackbar } from 'components/Snackbar';
import { useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { CompanyService } from 'services';
import { ICompany, ICompanyList } from 'types/Company';

import { AlertColor } from '@mui/material';

export interface ICompaniesContext {
  page: number;
  setPage: Function;
  maxPage: number;
  getCompany: Function;
  getCompanies: Function;
  getCompaniesCb: Function;
  deleteItem: Function;
  company: ICompany | undefined;
  companies: ICompanyList;
  loadingDelete: any;
  loadingView: boolean;
  loadingList: boolean;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export const CompaniesContext = createContext<ICompaniesContext>(
  {} as ICompaniesContext
);

type Props = {
  children: ReactNode;
};

export const CompaniesProvider = ({ children }: Props) => {
  const [companies, setCompanies] = useState<any>({
    count: 0,
    list: [],
    totalPages: 1,
  });

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);

  const loadingList = useLoader(true);
  const loadingView = useLoader();
  const loadingDelete = useLoader();

  const [inPage, setInPage] = useState(page);

  const [company, setCompany] = useState<ICompany>();

  const getCompanies = useCallback(async (page: number) => {
    try {
      const companies = await CompanyService.getCompaniesList(page, 10);
      return companies;
    } catch (error) {
      setMaxPage(1);
      setSnackbar({
        message: 'Não foi possível listar as empresas',
        variant: 'error',
      });
      return { list: [], count: 1, totalPages: 1 };
    }
  }, []);

  const getCompaniesCb = useCallback(
    async (force = false) => {
      if (inPage !== page || force === true) {
        const companies = await getCompanies(page);
        setCompanies(companies);
        setMaxPage(companies.totalPages);
      }
    },
    [setCompanies, inPage, page, getCompanies]
  );

  const getCompany = useCallback(async (id: string) => {
    if (!id) {
      return;
    }

    try {
      loadingView.showLoader();
      setCompany({} as ICompany);
      const companyView = await CompanyService.findCompanyById({ id });
      setCompany(companyView);
    } catch (reason) {
    } finally {
      loadingView.hideLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteItem = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      loadingDelete.showLoader();
      await CompanyService.deleteCompanyById(id);

      getCompaniesCb(true);

      setSnackbar({
        message: 'Empresa apagada com sucesso',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível apagar a empresa',
        variant: 'error',
      });
    } finally {
      loadingDelete.hideLoader();
    }
  };

  useEffect(() => {
    getCompanies(1).then((companies) => {
      loadingList.hideLoader();
      setCompanies(companies);
      setMaxPage(companies.totalPages);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCompanies, getCompanies]);

  useEffect(() => {
    setInPage(page);
  }, [page, setInPage, getCompaniesCb]);

  useEffect(() => {
    getCompaniesCb();
  }, [setInPage, getCompaniesCb]);

  return (
    <CompaniesContext.Provider
      value={{
        page,
        setPage,
        maxPage,
        getCompany,
        getCompanies,
        getCompaniesCb,
        deleteItem,
        company,
        companies,
        loadingDelete,
        loadingView: loadingView.loading,
        loadingList: loadingList.loading,
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </CompaniesContext.Provider>
  );
};
